<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NMessageProvider, NConfigProvider } from 'naive-ui'
import { gardenThemeOverrides } from '@/theme/garden'
import { NAIVE_LOCALES } from '@/theme/naiveLocale'
import { useEffectiveLocale } from '@/composables/useEffectiveLocale'
import { useLocaleSync } from '@/composables/useLocaleSync'
import { getKoraUrl } from '@architeia/auth'
import { getRuntimeConfig } from '@architeia/config'
import { KoraLegalLinks } from '@architeia/kora-account-menu'

useLocaleSync()
const { effectiveLocale } = useEffectiveLocale()
const naiveLocale = computed(() => NAIVE_LOCALES[effectiveLocale.value])

// ORG-34 (WCAG 2.4.1, 2.4.3): every page's main landmark is #tso-main. The skip link jumps there,
// and after an in-app navigation focus starts there instead of on the link that was activated.
const { t } = useI18n()
// KORA-189 — Terms, privacy notice and imprint on Kora Web, in every page's footer.
const koraWebOrigin: string = getRuntimeConfig().koraWebUrl ?? getKoraUrl()
const focusMain = () => document.getElementById('tso-main')?.focus()
// Optional: shell tests mount App without a router.
const router = useRouter() as ReturnType<typeof useRouter> | undefined
const stopFocusOnNavigation = router?.afterEach(async (to, from) => {
  // `from` matches nothing on the first navigation, which keeps the browser's focus.
  if (from.matched.length === 0 || to.path === from.path) return
  await nextTick()
  focusMain()
})
onBeforeUnmount(() => stopFocusOnNavigation?.())
</script>

<template>
  <NConfigProvider
    :theme-overrides="gardenThemeOverrides"
    :locale="naiveLocale.locale"
    :date-locale="naiveLocale.dateLocale"
  >
    <NMessageProvider>
      <a class="skip-link" href="#tso-main" @click.prevent="focusMain">{{
        t('nav.skipToContent')
      }}</a>
      <RouterView />
      <footer class="legal-footer" data-testid="legal-footer">
        <KoraLegalLinks :kora-web-origin="koraWebOrigin" :locale="effectiveLocale" />
      </footer>
    </NMessageProvider>
  </NConfigProvider>
</template>

<style scoped>
.skip-link {
  position: absolute;
  left: 8px;
  top: -48px;
  z-index: 1000;
  padding: 8px 16px;
  border-radius: 4px;
  background: var(--color-surface);
  color: var(--color-primary);
}

.skip-link:focus {
  top: 8px;
}

.legal-footer {
  padding: var(--space-4) var(--space-6) var(--space-6);
}
</style>
